import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import DropdownNewGap from '../DropdownNewGap';
import { Input, Segmented, MITREGrid, DayHourSelector } from './modalParts';

/* ---------------------------------------------------------------------------
 * Target validation utilities
 * ------------------------------------------------------------------------- */

function isValidIPv4(ip) {
  const r = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return r.test(ip);
}

function isValidIPv6(ip) {
  const cleanIp = ip.replace(/^\[|\]$/g, '');
  const r = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
  return r.test(cleanIp);
}

function isValidIPv4CIDR(cidr) {
  const r = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([0-9]|[1-2][0-9]|3[0-2])$/;
  return r.test(cidr);
}

function isValidIPv6CIDR(cidr) {
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;
  const prefix = parseInt(parts[1], 10);
  return isValidIPv6(parts[0]) && prefix >= 0 && prefix <= 128;
}

function isValidDomain(domain) {
  const cleanDomain = domain.replace(/^(https?:\/\/)?(www\.)?/, '');
  const domainOnly = cleanDomain.split('/')[0].split('?')[0];
  const r = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return r.test(domainOnly);
}

function isValidURL(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateNetworkTarget(target) {
  const trimmed = target.trim();
  if (!trimmed) return { valid: false, type: 'empty', value: trimmed };
  if (isValidIPv6CIDR(trimmed)) return { valid: true, type: 'ipv6_cidr', value: trimmed };
  if (isValidIPv4CIDR(trimmed)) return { valid: true, type: 'ipv4_cidr', value: trimmed };
  if (isValidIPv6(trimmed)) return { valid: true, type: 'ipv6', value: trimmed };
  if (isValidIPv4(trimmed)) return { valid: true, type: 'ipv4', value: trimmed };
  if (isValidDomain(trimmed)) return { valid: true, type: 'domain', value: trimmed };
  return { valid: false, type: 'invalid', value: trimmed };
}

function validateAppTarget(target) {
  const trimmed = target.trim();
  if (!trimmed) return { valid: false, type: 'empty', value: trimmed };
  if (isValidURL(trimmed)) return { valid: true, type: 'url', value: trimmed };
  if (isValidDomain(trimmed)) return { valid: true, type: 'domain', value: trimmed };
  return { valid: false, type: 'invalid', value: trimmed };
}

function parseAndValidate(input, validator) {
  if (!input.trim()) return { valid: [], invalid: [] };
  const lines = input.split(/[\n,]+/);
  const valid = [];
  const invalid = [];
  lines.forEach((line, index) => {
    const result = validator(line);
    if (result.valid) valid.push({ value: result.value, type: result.type, line: index + 1 });
    else if (result.type !== 'empty') invalid.push({ value: result.value, line: index + 1, reason: 'Invalid format' });
  });
  return { valid, invalid };
}

/* ---------------------------------------------------------------------------
 * Test-type metadata
 * ------------------------------------------------------------------------- */

const FRAMEWORK_MAP = { mitre_attack: 1, owasp: 2, nist_800_115: 3, custom: 4 };

// Subservice IDs: 1=Red Team, 2=NIT, 3=DAST, 4=SAST
const TYPE_CONFIG = {
  1: { // Red Team
    key: 'redteam',
    showNetworkTargets: true,
    showAppTargets: true,
    showSourceCode: false,
    showMitre: true,
    showFrameworkPicker: true,
    showTimeWindow: true,
    showCredentials: true,
    showRetest: true,
    defaultFramework: 'mitre_attack',
  },
  2: { // NIT
    key: 'nit',
    showNetworkTargets: true,
    showAppTargets: false,
    showSourceCode: false,
    showMitre: true,
    showFrameworkPicker: true,
    showTimeWindow: true,
    showCredentials: true,
    showRetest: true,
    defaultFramework: 'nist_800_115',
  },
  3: { // DAST
    key: 'dast',
    showNetworkTargets: false,
    showAppTargets: true,
    showSourceCode: false,
    showMitre: false,
    showFrameworkPicker: false,
    showTimeWindow: true,
    showCredentials: true,
    showRetest: true,
    defaultFramework: 'owasp',
  },
  4: { // SAST
    key: 'sast',
    showNetworkTargets: false,
    showAppTargets: false,
    showSourceCode: true,
    showMitre: false,
    showFrameworkPicker: false,
    showTimeWindow: false, // uses simple date range instead
    showCredentials: false,
    showRetest: false,
    defaultFramework: 'owasp',
  },
};

const STATUS_OPTIONS = [
  { id: 0, label: 'not started' },
  { id: 1, label: 'in progress' },
  { id: 2, label: 'finished' },
];

const ALLOWED_ROE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
];

/* ---------------------------------------------------------------------------
 * CreateTestModal
 * ------------------------------------------------------------------------- */

function CreateTestModal({
  open,
  onClose,
  onCreated,
  onNotify,
  userRole,
  customers,
  subservices,
  engagement: editEngagement,
}) {
  const isEditMode = Boolean(editEngagement);
  const isLocked = isEditMode && editEngagement.status === 'in progress';
  // Basic engagement info
  const [engagementType, setEngagementType] = useState(1);
  const [engagementName, setEngagementName] = useState('');
  const [engagementDescription, setEngagementDescription] = useState('');
  const [engagementMode, setEngagementMode] = useState('autonomous');
  const [engagementFramework, setEngagementFramework] = useState('mitre_attack');
  const [engagementCustomer, setEngagementCustomer] = useState(null);
  const [comments, setComments] = useState('');
  const [statusOverride, setStatusOverride] = useState(null); // null = auto

  // Network / app targets
  const [externalTargetsInput, setExternalTargetsInput] = useState('');
  const [externalTargetsValidation, setExternalTargetsValidation] = useState({ valid: [], invalid: [] });
  const [networkTargetsInput, setNetworkTargetsInput] = useState('');
  const [networkTargetsValidation, setNetworkTargetsValidation] = useState({ valid: [], invalid: [] });
  const [appTargetsInput, setAppTargetsInput] = useState('');
  const [appTargetsValidation, setAppTargetsValidation] = useState({ valid: [], invalid: [] });
  const [excludedInput, setExcludedInput] = useState('');
  const [excludedValidation, setExcludedValidation] = useState({ valid: [], invalid: [] });

  // SAST-specific (repo + language)
  const [sastRepo, setSastRepo] = useState('');
  const [sastLanguage, setSastLanguage] = useState('');
  const [sastExcludedPaths, setSastExcludedPaths] = useState('');

  // Credentials
  const [credentialsProvided, setCredentialsProvided] = useState(false);

  // MITRE tactics depth
  const [selectedDepth, setSelectedDepth] = useState(3);

  // Time windows (for non-SAST types)
  const [timeWindows, setTimeWindows] = useState({});

  // Simple date range (for SAST)
  const [sastStart, setSastStart] = useState('');
  const [sastFinish, setSastFinish] = useState('');

  // Retest
  const [retestEnabled, setRetestEnabled] = useState(false);
  const [retestWindowDays, setRetestWindowDays] = useState(30);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(true);

  // Files & permissions
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [showInvalidFileModal, setShowInvalidFileModal] = useState(false);
  const [existingRoeText, setExistingRoeText] = useState('');
  const [permissionLevel, setPermissionLevel] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  const config = TYPE_CONFIG[engagementType] || TYPE_CONFIG[1];

  // Reset framework when test type changes to one that forces it
  useEffect(() => {
    if (config && !config.showFrameworkPicker) {
      setEngagementFramework(config.defaultFramework);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementType]);

  // Pre-select the user's org if they're a regular user
  useEffect(() => {
    if (userRole === 'user' && customers?.length > 0 && !engagementCustomer) {
      setEngagementCustomer(customers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, customers]);

  // Populate form when editing an existing engagement
  useEffect(() => {
    if (!editEngagement || !open) return;
    const eng = editEngagement;
    const scope = eng.offensive_scope;

    setEngagementType(eng.subtype_id || 1);
    setEngagementName(eng.title || '');
    setEngagementDescription(eng.description || '');
    setEngagementMode(eng.mode || 'autonomous');
    setEngagementCustomer(eng.customer_id || eng.customer?.id || null);
    setComments(eng.comments || '');

    // Framework — reverse-map from scope
    const FRAMEWORK_REVERSE = { mitre_attack: 'mitre_attack', owasp: 'owasp', nist_800_115: 'nist_800_115', custom: 'custom' };
    if (scope?.offensive_framework) {
      setEngagementFramework(FRAMEWORK_REVERSE[scope.offensive_framework] || 'mitre_attack');
    }

    // Status
    const STATUS_REVERSE = { 'not started': 0, 'in progress': 1, 'finished': 2 };
    setStatusOverride(STATUS_REVERSE[eng.status] ?? null);

    // Credentials
    setCredentialsProvided(scope?.credentials_provided || false);

    // AI config
    let aiConfig = {};
    if (scope?.ai_config) {
      try { aiConfig = typeof scope.ai_config === 'string' ? JSON.parse(scope.ai_config) : scope.ai_config; } catch (e) { /* ignore */ }
    }
    setSelectedDepth(aiConfig.orchestration?.max_depth || aiConfig.max_depth || 3);

    // Targets — parse JSON arrays back to comma-separated input
    const parseJsonArray = (val) => {
      if (!val) return '';
      try {
        const arr = typeof val === 'string' ? JSON.parse(val) : val;
        return Array.isArray(arr) ? arr.join(', ') : '';
      } catch { return ''; }
    };

    const extTargets = parseJsonArray(scope?.external_targets);
    setExternalTargetsInput(extTargets);
    if (extTargets) setExternalTargetsValidation({ valid: extTargets.split(',').map(s => s.trim()).filter(Boolean).map(v => ({ value: v })), invalid: [] });

    const netTargets = parseJsonArray(scope?.target_networks);
    setNetworkTargetsInput(netTargets);
    if (netTargets) setNetworkTargetsValidation({ valid: netTargets.split(',').map(s => s.trim()).filter(Boolean).map(v => ({ value: v })), invalid: [] });

    const appTargets = parseJsonArray(scope?.target_applications);
    setAppTargetsInput(appTargets);
    if (appTargets) setAppTargetsValidation({ valid: appTargets.split(',').map(s => s.trim()).filter(Boolean).map(v => ({ value: v })), invalid: [] });

    const excluded = parseJsonArray(scope?.excluded_targets);
    setExcludedInput(excluded);
    if (excluded) setExcludedValidation({ valid: excluded.split(',').map(s => s.trim()).filter(Boolean).map(v => ({ value: v })), invalid: [] });

    // Timeline / testing window
    if (scope?.testing_window) {
      try {
        const tw = typeof scope.testing_window === 'string' ? JSON.parse(scope.testing_window) : scope.testing_window;
        if (tw.allowed_windows && typeof tw.allowed_windows === 'object') {
          setTimeWindows(tw.allowed_windows);
        } else if (tw.start && tw.end) {
          setSastStart(tw.start);
          setSastFinish(tw.end);
        }
      } catch { /* ignore */ }
    } else if (eng.start && eng.finish) {
      setSastStart(eng.start);
      setSastFinish(eng.finish);
    }

    // Retest
    setRetestEnabled(scope?.retest_enabled || false);
    if (scope?.retest_config) {
      try {
        const rc = typeof scope.retest_config === 'string' ? JSON.parse(scope.retest_config) : scope.retest_config;
        if (rc.retest_window_days) setRetestWindowDays(rc.retest_window_days);
        if (rc.approval_required !== undefined) setApprovalRequired(rc.approval_required);
        if (rc.notify_on_completion !== undefined) setNotifyOnCompletion(rc.notify_on_completion);
      } catch { /* ignore */ }
    }

    // Files — can't recreate File objects, but show that RoE text was extracted
    setUploadedFiles([]);
    setExistingRoeText(aiConfig.roe_text || '');
    setPermissionLevel(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEngagement, open]);

  // Detect backdated engagement
  const isBackdated = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (config.showTimeWindow) {
      const selectedDates = Object.keys(timeWindows);
      if (selectedDates.length === 0) return false;
      return selectedDates.some((d) => new Date(d) < today);
    }
    // SAST date range
    if (sastStart) return new Date(sastStart) < today;
    return false;
  }, [timeWindows, sastStart, config.showTimeWindow]);

  // Effective status: if backdated and no override, default to 'finished'
  const effectiveStatus = statusOverride !== null
    ? statusOverride
    : isBackdated ? 2 : 0;

  const resetForm = () => {
    setEngagementType(1);
    setEngagementName('');
    setEngagementDescription('');
    setEngagementMode('autonomous');
    setEngagementFramework('mitre_attack');
    setEngagementCustomer(null);
    setComments('');
    setStatusOverride(null);
    setExternalTargetsInput('');
    setExternalTargetsValidation({ valid: [], invalid: [] });
    setNetworkTargetsInput('');
    setNetworkTargetsValidation({ valid: [], invalid: [] });
    setAppTargetsInput('');
    setAppTargetsValidation({ valid: [], invalid: [] });
    setExcludedInput('');
    setExcludedValidation({ valid: [], invalid: [] });
    setSastRepo('');
    setSastLanguage('');
    setSastExcludedPaths('');
    setCredentialsProvided(false);
    setSelectedDepth(3);
    setTimeWindows({});
    setSastStart('');
    setSastFinish('');
    setRetestEnabled(false);
    setRetestWindowDays(30);
    setApprovalRequired(true);
    setNotifyOnCompletion(true);
    setUploadedFiles([]);
    setExistingRoeText('');
    setPermissionLevel(0);
  };

  const handleClose = () => {
    resetForm();
    onClose && onClose();
  };

  /* ---------- File handling ---------- */
  const handleFileUpload = (e) => {
    const fileList = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (fileList && fileList.length > 0) {
      const invalid = Array.from(fileList).filter((f) => !ALLOWED_ROE_TYPES.includes(f.type));
      const valid = Array.from(fileList).filter((f) => ALLOWED_ROE_TYPES.includes(f.type));
      if (invalid.length > 0) {
        setInvalidFiles(invalid.map((f) => f.name));
        setShowInvalidFileModal(true);
      }
      if (valid.length > 0) setUploadedFiles(valid.slice(0, 1));
    }
  };

  const handleFileDelete = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileUpload(e);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /* ---------- Target validation handlers ---------- */
  const handleExternalTargetsChange = (e) => {
    const input = e.target.value;
    setExternalTargetsInput(input);
    setExternalTargetsValidation(parseAndValidate(input, validateNetworkTarget));
  };

  const handleNetworkTargetsChange = (e) => {
    const input = e.target.value;
    setNetworkTargetsInput(input);
    setNetworkTargetsValidation(parseAndValidate(input, validateNetworkTarget));
  };

  const handleAppTargetsChange = (e) => {
    const input = e.target.value;
    setAppTargetsInput(input);
    setAppTargetsValidation(parseAndValidate(input, validateAppTarget));
  };

  const handleExcludedChange = (e) => {
    const input = e.target.value;
    setExcludedInput(input);
    // Excluded targets can be either network or app depending on test type
    const validator = config.showAppTargets && !config.showNetworkTargets
      ? validateAppTarget
      : validateNetworkTarget;
    setExcludedValidation(parseAndValidate(input, validator));
  };

  /* ---------- Submission ---------- */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const notify = (msg, type = 'error') => onNotify && onNotify(msg, type);

    // Validation
    if (!engagementName.trim()) {
      notify('Test name is required');
      return;
    }
    if (!engagementCustomer) {
      notify('Organization is required');
      return;
    }

    // Gather targets based on test type
    const targetsList = [];
    const externalTargetsList = [];
    if (config.showNetworkTargets) {
      if (externalTargetsValidation.invalid.length > 0) {
        notify(`${externalTargetsValidation.invalid.length} invalid external targets. Please fix them.`);
        return;
      }
      externalTargetsList.push(...externalTargetsValidation.valid.map((t) => t.value));
      if (networkTargetsValidation.invalid.length > 0) {
        notify(`${networkTargetsValidation.invalid.length} invalid internal network targets. Please fix them.`);
        return;
      }
      targetsList.push(...networkTargetsValidation.valid.map((t) => t.value));
    }
    if (config.showAppTargets) {
      if (appTargetsValidation.invalid.length > 0) {
        notify(`${appTargetsValidation.invalid.length} invalid application targets. Please fix them.`);
        return;
      }
      targetsList.push(...appTargetsValidation.valid.map((t) => t.value));
    }
    if (config.showSourceCode) {
      if (!sastRepo.trim()) {
        notify('Source repository / location is required for SAST');
        return;
      }
      targetsList.push(sastRepo.trim());
    }

    if (targetsList.length === 0 && externalTargetsList.length === 0) {
      notify('At least one valid target is required');
      return;
    }

    if (excludedValidation.invalid.length > 0) {
      notify(`${excludedValidation.invalid.length} invalid excluded targets. Please fix them.`);
      return;
    }

    // Determine start/finish
    let start, finish, timelineWindows;
    if (config.showTimeWindow) {
      if (!timeWindows || Object.keys(timeWindows).length === 0) {
        notify('You must select at least one day in the testing window');
        return;
      }
      const dates = Object.keys(timeWindows).sort();
      start = dates[0];
      finish = dates[dates.length - 1];
      timelineWindows = timeWindows;
    } else {
      if (!sastStart || !sastFinish) {
        notify('Start and finish dates are required');
        return;
      }
      if (new Date(sastFinish) < new Date(sastStart)) {
        notify('Finish date must be after start date');
        return;
      }
      start = sastStart;
      finish = sastFinish;
      timelineWindows = {};
    }

    const excludedTargets = config.showSourceCode
      ? sastExcludedPaths.split('\n').map((s) => s.trim()).filter(Boolean)
      : excludedValidation.valid.map((t) => t.value);

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      const engagementData = {
        subtype_id: engagementType,
        framework_id: FRAMEWORK_MAP[engagementFramework] || 1,
        title: engagementName,
        customer_id: engagementCustomer,
        start,
        finish,
        comments: comments || (config.showSourceCode && sastLanguage ? `Language/framework: ${sastLanguage}` : ''),
        status: STATUS_OPTIONS[effectiveStatus].label,
        permission_level: permissionLevel,
        name: engagementName,
        description: engagementDescription,
        mode: engagementMode,
        framework: engagementFramework,
        timeline: {
          start,
          end: finish,
          allowed_windows: timelineWindows,
        },
        settings: {
          autoProgress: true,
          parallelExecution: false,
          maxConcurrentTechniques: 3,
          outputLevel: 'detailed',
        },
        ai_config: {
          orchestration: {
            max_depth: config.showMitre ? selectedDepth : 1,
            max_parallel_tasks: 3,
          },
        },
      };

      const scopeData = {
        targets: targetsList,
        external_targets: externalTargetsList,
        excluded_targets: excludedTargets,
        objectives: [],
        constraints: [],
        credentials_provided: credentialsProvided,
        test_type_key: config.key, // for future BE use
        sast_language: config.showSourceCode ? sastLanguage : null,
        retest_enabled: retestEnabled,
        retest_config: retestEnabled
          ? {
              retest_window_days: retestWindowDays,
              approval_required: approvalRequired,
              notify_on_completion: notifyOnCompletion,
            }
          : null,
      };

      formData.append('engagementData', JSON.stringify(engagementData));
      formData.append('scopeData', JSON.stringify(scopeData));

      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => formData.append('files', file));
      }

      if (isEditMode) {
        // Update engagement + scope separately
        const authHeaders = { access_token: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const opts = { headers: authHeaders, withCredentials: true };

        await axios.put(`${Config.APIBaseURL}/engagement/update/${editEngagement.id}`, {
          title: engagementData.title,
          start: engagementData.start,
          finish: engagementData.finish,
          comments: engagementData.comments,
          status: engagementData.status,
        }, opts);

        await axios.put(`${Config.APIBaseURL}/engagement/offensive/${editEngagement.id}/scope`, {
          target_networks: JSON.stringify(targetsList),
          external_targets: externalTargetsList.length ? JSON.stringify(externalTargetsList) : null,
          excluded_targets: excludedTargets.length ? JSON.stringify(excludedTargets) : null,
          credentials_provided: credentialsProvided,
          offensive_framework: FRAMEWORK_MAP[engagementFramework] ? engagementFramework : 'mitre_attack',
          testing_window: JSON.stringify({ start, end: finish, allowed_windows: timelineWindows }),
          ai_config: JSON.stringify({
            max_depth: config.showMitre ? selectedDepth : 1,
            parallel_tasks: 3,
          }),
          retest_enabled: retestEnabled,
          retest_config: scopeData.retest_config ? JSON.stringify(scopeData.retest_config) : null,
        }, opts);

        // Upload new RoE file if provided
        if (uploadedFiles && uploadedFiles.length > 0) {
          const roeFormData = new FormData();
          roeFormData.append('file', uploadedFiles[0]);
          await axios.put(
            `${Config.APIBaseURL}/engagement/offensive/${editEngagement.id}/roe`,
            roeFormData,
            { headers: { 'Content-Type': 'multipart/form-data', access_token: `Bearer ${token}` }, withCredentials: true }
          );
        }

        notify('Engagement updated successfully', 'success');
        resetForm();
        onCreated && onCreated();
        onClose && onClose();
      } else {
        const response = await axios.post(`${Config.APIBaseURL}/engagement/offensive/add/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            access_token: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        if (response.status === 200 || response.status === 201) {
          notify('Engagement created successfully', 'success');
          resetForm();
          onCreated && onCreated();
          onClose && onClose();
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} offensive engagement:`, error);
      notify(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} engagement`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose}></div>
        <div className="relative w-full max-w-[1200px] max-h-[92vh] overflow-auto rounded-2xl bg-slate-900/95 shadow-2xl border border-slate-800 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-xl font-semibold text-white">{isEditMode ? 'Edit Engagement' : 'New Engagement'}</h3>
              <p className="text-xs text-slate-500 mt-1">
                {isLocked ? 'This engagement is in progress and cannot be modified' : isEditMode ? 'Modify engagement configuration' : 'Configure a new offensive security engagement'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="text-sm px-3 py-1.5 rounded-md bg-slate-800 text-slate-300 hover:bg-slate-700"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Test type selector */}
          <div className="mb-5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Test Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {subservices.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => !isEditMode && setEngagementType(sub.id)}
                  disabled={isEditMode}
                  className={`p-3 rounded-lg border-2 transition text-left ${
                    engagementType === sub.id
                      ? 'border-indigo-500 bg-indigo-600/10'
                      : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'
                  } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className={`text-sm font-semibold ${
                    engagementType === sub.id ? 'text-indigo-400' : 'text-slate-300'
                  }`}>
                    {sub.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{sub.description}</div>
                </button>
              ))}
            </div>
            {subservices.length === 0 && (
              <div className="text-sm text-slate-500 mt-2">Loading test types...</div>
            )}
          </div>

          {/* Backdated banner */}
          {isBackdated && (
            <div className="mb-5 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-300">Back-dated engagement</div>
                  <div className="text-xs text-amber-200/80 mt-0.5">
                    This engagement starts in the past. Its status will default to{' '}
                    <span className="font-semibold">finished</span> unless you change it below.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Basic info section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="space-y-3 placeholder:text-slate-500">
              <Input label="Name" value={engagementName} onChange={setEngagementName} placeholder="e.g. Acme Corp Q2 Pentest" />
              <div>
                <label className="text-sm text-slate-300">Description</label>
                <textarea
                  className="mt-2 w-full h-20 rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm placeholder:text-slate-500"
                  value={engagementDescription}
                  onChange={(e) => setEngagementDescription(e.target.value)}
                  placeholder="Scope, goals, notable context..."
                />
              </div>
            </div>
            <div className="space-y-3">
              <Segmented
                label="Execution Mode"
                value={engagementMode}
                onChange={setEngagementMode}
                options={['autonomous', 'manual']}
                labels={{ autonomous: 'Autonomous', manual: 'Manual' }}
              />
              {config.showFrameworkPicker && (
                <Segmented
                  label="Framework"
                  value={engagementFramework}
                  onChange={setEngagementFramework}
                  options={['mitre_attack', 'owasp', 'nist_800_115', 'custom']}
                  labels={{
                    mitre_attack: 'MITRE ATT&CK',
                    owasp: 'OWASP',
                    nist_800_115: 'NIST 800-115',
                    custom: 'Custom',
                  }}
                />
              )}
              <div>
                <label className="text-sm text-slate-300">Organization</label>
                {userRole === 'user' || isEditMode ? (
                  <div className="mt-2 w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-400 text-sm">
                    {isEditMode
                      ? (editEngagement.customer?.name || customers.find(c => c.id === engagementCustomer)?.label || 'Organization')
                      : (customers.length > 0
                        ? customers[0]?.label
                        : JSON.parse(localStorage.getItem('userinfo') || '{}')?.org_name || 'Your organization')}
                  </div>
                ) : (
                  <div className="mt-2">
                    <DropdownNewGap
                      options={customers}
                      style="w-full"
                      onChange={setEngagementCustomer}
                      value={engagementCustomer}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* External targets (public, directly reachable) */}
          {config.showNetworkTargets && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-1">External Targets</label>
              <div className="text-xs text-slate-500 mb-2">
                Public IPs, CIDRs, or hostnames directly reachable from the internet — one per line
              </div>
              <textarea
                className={`w-full h-24 rounded-md bg-slate-950/70 border p-3 text-slate-200 text-sm placeholder:text-slate-500 font-mono ${
                  externalTargetsValidation.invalid.length > 0
                    ? 'border-rose-500/60 focus:border-rose-500 focus:ring-1 focus:ring-rose-500'
                    : 'border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                }`}
                placeholder={'203.0.113.10\nexample.com\n198.51.100.0/24'}
                value={externalTargetsInput}
                onChange={handleExternalTargetsChange}
              />
              {externalTargetsValidation.valid.length > 0 && (
                <div className="text-xs text-emerald-400 mt-1">
                  {externalTargetsValidation.valid.length} valid target{externalTargetsValidation.valid.length === 1 ? '' : 's'}
                </div>
              )}
              {externalTargetsValidation.invalid.length > 0 && (
                <div className="text-xs text-rose-400 mt-1">
                  {externalTargetsValidation.invalid.length} invalid target{externalTargetsValidation.invalid.length === 1 ? '' : 's'} — please fix
                </div>
              )}
            </div>
          )}

          {/* Internal network targets (private, VPN-routed through agent VM) */}
          {config.showNetworkTargets && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-1">Internal Network Targets</label>
              <div className="text-xs text-slate-500 mb-2">
                Private IPs or CIDRs reachable only through the deployed agent VM (VPN-routed) — one per line
              </div>
              <textarea
                className={`w-full h-24 rounded-md bg-slate-950/70 border p-3 text-slate-200 text-sm placeholder:text-slate-500 font-mono ${
                  networkTargetsValidation.invalid.length > 0
                    ? 'border-rose-500/60 focus:border-rose-500 focus:ring-1 focus:ring-rose-500'
                    : 'border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                }`}
                placeholder={'192.168.1.0/24\n10.0.0.0/16\n172.16.0.0/12'}
                value={networkTargetsInput}
                onChange={handleNetworkTargetsChange}
              />
              {networkTargetsValidation.valid.length > 0 && (
                <div className="text-xs text-emerald-400 mt-1">
                  {networkTargetsValidation.valid.length} valid target{networkTargetsValidation.valid.length === 1 ? '' : 's'}
                </div>
              )}
              {networkTargetsValidation.invalid.length > 0 && (
                <div className="text-xs text-rose-400 mt-1">
                  {networkTargetsValidation.invalid.length} invalid target{networkTargetsValidation.invalid.length === 1 ? '' : 's'} — please fix
                </div>
              )}
            </div>
          )}

          {/* Application targets */}
          {config.showAppTargets && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-1">Application URLs</label>
              <div className="text-xs text-slate-500 mb-2">
                URLs or domains — one per line
              </div>
              <textarea
                className={`w-full h-24 rounded-md bg-slate-950/70 border p-3 text-slate-200 text-sm placeholder:text-slate-500 font-mono ${
                  appTargetsValidation.invalid.length > 0
                    ? 'border-rose-500/60 focus:border-rose-500 focus:ring-1 focus:ring-rose-500'
                    : 'border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                }`}
                placeholder={'https://app.example.com\nhttps://api.example.com/v1'}
                value={appTargetsInput}
                onChange={handleAppTargetsChange}
              />
              {appTargetsValidation.valid.length > 0 && (
                <div className="text-xs text-emerald-400 mt-1">
                  {appTargetsValidation.valid.length} valid URL{appTargetsValidation.valid.length === 1 ? '' : 's'}
                </div>
              )}
              {appTargetsValidation.invalid.length > 0 && (
                <div className="text-xs text-rose-400 mt-1">
                  {appTargetsValidation.invalid.length} invalid URL{appTargetsValidation.invalid.length === 1 ? '' : 's'} — please fix
                </div>
              )}
            </div>
          )}

          {/* SAST-specific: source code repo + language */}
          {config.showSourceCode && (
            <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Source Repository / Location"
                value={sastRepo}
                onChange={setSastRepo}
                placeholder="git@github.com:acme/repo.git or /path/to/snapshot"
              />
              <Input
                label="Language / Framework"
                value={sastLanguage}
                onChange={setSastLanguage}
                placeholder="Python 3.11 / Django 4.2"
              />
            </div>
          )}

          {/* Excluded targets */}
          {(config.showNetworkTargets || config.showAppTargets) && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-1">Excluded Targets</label>
              <div className="text-xs text-slate-500 mb-2">Out-of-scope targets (optional)</div>
              <textarea
                className={`w-full h-20 rounded-md bg-slate-950/70 border p-3 text-slate-200 text-sm placeholder:text-slate-500 font-mono ${
                  excludedValidation.invalid.length > 0
                    ? 'border-rose-500/60 focus:border-rose-500 focus:ring-1 focus:ring-rose-500'
                    : 'border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                }`}
                placeholder={'production.example.com\n10.0.0.1'}
                value={excludedInput}
                onChange={handleExcludedChange}
              />
              {excludedValidation.invalid.length > 0 && (
                <div className="text-xs text-rose-400 mt-1">
                  {excludedValidation.invalid.length} invalid — please fix
                </div>
              )}
            </div>
          )}

          {/* SAST excluded paths */}
          {config.showSourceCode && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-1">Excluded Paths</label>
              <div className="text-xs text-slate-500 mb-2">Glob patterns or directory paths (optional)</div>
              <textarea
                className="w-full h-20 rounded-md bg-slate-950/70 border border-slate-800 p-3 text-slate-200 text-sm placeholder:text-slate-500 font-mono focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                placeholder={'**/tests/**\nnode_modules/\n*.generated.ts'}
                value={sastExcludedPaths}
                onChange={(e) => setSastExcludedPaths(e.target.value)}
              />
            </div>
          )}

          {/* Credentials toggle */}
          {config.showCredentials && (
            <div className="mb-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCredentialsProvided(!credentialsProvided)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  credentialsProvided ? 'bg-indigo-600' : 'bg-slate-700'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    credentialsProvided ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <label className="text-sm text-slate-200 font-medium">Credentials Provided</label>
                <p className="text-xs text-slate-500">Client has shared test accounts / credentials for authenticated testing</p>
              </div>
            </div>
          )}

          {/* MITRE tactics depth */}
          {config.showMitre && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300">MITRE ATT&CK Tactic Depth</label>
              <div className="text-xs text-slate-500 mt-0.5">
                Click a tactic to set how far through the kill chain this engagement should go
              </div>
              <MITREGrid selectedDepth={selectedDepth} onDepthChange={setSelectedDepth} />
            </div>
          )}

          {/* Testing window (calendar + hours) OR SAST date range */}
          <div className="mb-5">
            {config.showTimeWindow ? (
              <>
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-300">Testing Window</label>
                    <div className="text-xs text-slate-500">
                      Select days and hours when testing is allowed. Past days appear in amber.
                    </div>
                  </div>
                </div>
                <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-3">
                  <DayHourSelector value={timeWindows} onChange={setTimeWindows} allowPast />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300">Start Date</label>
                  <input
                    type="date"
                    value={sastStart}
                    onChange={(e) => setSastStart(e.target.value)}
                    className="mt-2 w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300">Finish Date</label>
                  <input
                    type="date"
                    value={sastFinish}
                    onChange={(e) => setSastFinish(e.target.value)}
                    className="mt-2 w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status selector (only when backdated) */}
          {isBackdated && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setStatusOverride(opt.id)}
                    className={`px-3 py-2 rounded-md text-sm capitalize transition ${
                      effectiveStatus === opt.id
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-900/60 text-slate-200 border border-slate-800 hover:bg-slate-800/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* RoE upload */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-300 mb-2">Rules of Engagement (RoE)</label>
            <div
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('create-test-file-upload').click()}
              className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-500/60 transition"
            >
              <input
                type="file"
                id="create-test-file-upload"
                className="hidden"
                onChange={handleFileUpload}
                accept={ALLOWED_ROE_TYPES.join(',')}
              />
              <svg className="w-8 h-8 mx-auto text-slate-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-400">
                Upload signed RoE document
              </p>
              <p className="text-xs text-slate-600 mt-1">
                PDF, DOCX, or TXT (1 file max)
              </p>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="mt-2 space-y-1">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 bg-slate-900/50 rounded-md border border-slate-800"
                  >
                    <span className="text-xs text-slate-300 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={(e) => handleFileDelete(e, index)}
                      className="text-xs text-slate-500 hover:text-rose-400"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {isEditMode && existingRoeText && uploadedFiles.length === 0 && (
              <div className="mt-2 px-3 py-2 bg-emerald-900/20 rounded-md border border-emerald-800/40 flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-emerald-300">RoE document previously uploaded ({existingRoeText.length.toLocaleString()} chars extracted). Upload a new file to replace it.</span>
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-300 mb-2">Visibility</label>
            <div className="flex gap-2">
              {[
                { id: 0, label: 'Only me' },
                { id: 1, label: 'Org read' },
                { id: 2, label: 'Org read + write' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPermissionLevel(opt.id)}
                  className={`px-3 py-2 rounded-md text-sm transition ${
                    permissionLevel === opt.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-900/60 text-slate-200 border border-slate-800 hover:bg-slate-800/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Retest section */}
          {config.showRetest && (
            <div className="mb-5 rounded-lg border border-slate-800 bg-slate-950/50 p-4">
              <div className="flex items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setRetestEnabled(!retestEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    retestEnabled ? 'bg-indigo-600' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      retestEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <div>
                  <label className="text-sm text-slate-200 font-medium">Include Retest</label>
                  <p className="text-xs text-slate-500">Mark findings for retest after remediation</p>
                </div>
              </div>

              {retestEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="text-xs text-slate-400">Retest window (days)</label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={retestWindowDays}
                      onChange={(e) => setRetestWindowDays(parseInt(e.target.value) || 30)}
                      className="mt-1 w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200 text-sm"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={approvalRequired}
                      onChange={(e) => setApprovalRequired(e.target.checked)}
                      className="rounded border-slate-700 bg-slate-900"
                    />
                    Requires approval
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={notifyOnCompletion}
                      onChange={(e) => setNotifyOnCompletion(e.target.checked)}
                      className="rounded border-slate-700 bg-slate-900"
                    />
                    Notify on completion
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              className="text-sm px-5 py-2 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting || isLocked}
              className="text-sm px-5 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleCreate}
            >
              {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Engagement' : 'Create Engagement')}
            </button>
          </div>
        </div>
      </div>

      {/* Invalid files modal */}
      {showInvalidFileModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[60]">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowInvalidFileModal(false)}></div>
          <div className="relative max-w-sm p-4 bg-slate-800 rounded-lg shadow-lg border border-slate-700">
            <div className="text-sm text-slate-200">
              <p className="font-semibold text-amber-300 mb-2">Some files were rejected</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 mb-3">
                {invalidFiles.map((fileName, index) => (
                  <li key={index}>{fileName}</li>
                ))}
              </ul>
              <p className="text-xs text-slate-400">
                Only documents (PDF, DOCX, XLSX, CSV) and images are allowed.
              </p>
            </div>
            <div className="flex justify-end mt-3">
              <button
                className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
                onClick={() => {
                  setShowInvalidFileModal(false);
                  setInvalidFiles([]);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateTestModal;
